import { useEffect, useState } from 'react';
import { mapaEmbebido } from '../lib/mapa.js';

/**
 * De acá para arriba el mapa va en una columna al costado.
 *
 * El mismo número está en la media query de global.css, y no hay forma linda
 * de tener uno solo sin un preprocesador. Si cambia, cambia en los dos.
 */
const PANTALLA_ANCHA = '(min-width: 900px)';

/**
 * ¿Estamos en una pantalla ancha?
 *
 * Hace falta en JavaScript y no alcanza con esconder la columna por CSS: un
 * iframe con display:none igual se crea y el navegador igual le pide la página
 * a Google. En un celular eso sería exactamente lo que esta pantalla quiere
 * evitar —que Google vea a todo el que entra a reservar—, así que abajo de 900
 * el componente no devuelve nada en vez de devolver algo escondido.
 */
function usaPantallaAncha() {
  const [ancha, setAncha] = useState(() => window.matchMedia(PANTALLA_ANCHA).matches);

  useEffect(() => {
    const consulta = window.matchMedia(PANTALLA_ANCHA);
    const alCambiar = (evento) => setAncha(evento.matches);
    // Otra vez al montar: entre el primer render y el efecto puede haber
    // rotado el teléfono.
    setAncha(consulta.matches);
    consulta.addEventListener('change', alCambiar);
    return () => consulta.removeEventListener('change', alCambiar);
  }, []);

  return ancha;
}

/**
 * El iframe del mapa, enmarcado. Sin título ni dirección alrededor.
 *
 * Existe aparte de Mapa porque el panel lo usa como vista previa del campo
 * dirección, donde un «Dónde queda» en grande no pinta nada. Lo que comparten
 * —y es lo que importa que no se separe— es cómo se arma la URL y que el
 * iframe sea perezoso.
 *
 * El marco es un div y no el iframe a secas porque el iframe no respeta
 * border-radius en todos los navegadores: el que recorta es el div.
 */
export function MarcoDeMapa({ direccion, titulo, className = 'mapa-marco', perezoso = true }) {
  return (
    <div className={className}>
      {/* El key es a propósito. Cambiarle el src a un iframe que ya existe deja
          una entrada en el historial del navegador, así que el que escribe su
          dirección en el panel y después toca «atrás» se pasea por versiones
          viejas del mapa en vez de salir de la página. Con el key, React tira
          el iframe y pone uno nuevo, que no ensucia el historial. */}
      <iframe
        key={direccion}
        src={mapaEmbebido(direccion)}
        title={titulo}
        // perezoso={false} para el que despliega el botón de la cabecera: ese
        // iframe no existe hasta que alguien lo pide, así que ya no hay nada
        // que ahorrar y `lazy` sólo agregaría un recuadro vacío entre el toque
        // y el mapa.
        loading={perezoso ? 'lazy' : 'eager'}
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
}

/**
 * La columna del mapa, al costado del flujo de reserva.
 *
 * Se dibuja sólo si hay dirección: quien atiende a domicilio no tiene un punto
 * que mostrar, y un mapa del centro de cualquier lado sería peor que nada.
 *
 * ─── Dónde aparece y dónde no ──────────────────────────────────────────────
 *
 * Esto es la versión de pantalla ancha, y abajo de 900px no se dibuja —no se
 * esconde, no existe—: en un celular una columna al costado no tiene lugar, y
 * el mapa de ahí lo despliega el botón «Ver mapa» de la cabecera. Son dos
 * lugares distintos para el mismo marco, no dos mapas.
 *
 * Al costado y no al final del flujo porque es una referencia, no un paso: se
 * mira mientras se elige el horario, y abajo de todo había que ir a buscarla.
 * Queda sticky por lo mismo: la página se hace larga eligiendo día y hora.
 *
 * Y no reemplaza a nada: la dirección en texto y el botón «Cómo llegar» están
 * en la cabecera y no dependen de Google. Si el iframe algún día falla —ver la
 * advertencia en lib/mapa.js— la página sigue contestando dónde queda.
 */
export function Mapa({ direccion, nombre }) {
  const ancha = usaPantallaAncha();
  const limpia = direccion?.trim();
  if (!limpia || !ancha) return null;

  return (
    <aside className="mapa" aria-labelledby="mapa-t">
      <h2 className="mapa-titulo" id="mapa-t">Dónde queda</h2>
      <p className="mapa-direccion">{limpia}</p>
      {/* El título del iframe no es decorativo: es lo que anuncia un lector de
          pantalla al llegar a este marco, y «mapa» a secas no dice de dónde. */}
      <MarcoDeMapa direccion={limpia} titulo={`Mapa de ${nombre ?? 'el negocio'}, en ${limpia}`} />
    </aside>
  );
}
